/**
 * useSubscription — wraps trpc.stripe.getSubscriptionStatus
 *
 * The server enforces an owner/admin bypass: if the authenticated user is the
 * project owner (OWNER_OPEN_ID env var) or has role='admin', the server returns
 * { subscriptionTier: 'trainer', subscriptionStatus: 'active', isOwner: true }
 * regardless of Stripe status. This hook surfaces that as full access.
 *
 * Derived booleans:
 *   isOwner    — server confirmed owner/admin bypass (no Stripe check needed)
 *   isPaid     — any active paid plan OR owner bypass
 *   isFounder  — founding_monthly or founding_lifetime (or owner)
 *   isTrainer  — trainer tier (or owner)
 *   isLifetime — founding_lifetime (no renewal needed)
 *   isLoading  — query in-flight
 */
import { trpc } from "@/lib/trpc";

export type SubscriptionTier =
  | "free"
  | "founding_monthly"
  | "founding_lifetime"
  | "trainer";

export type SubscriptionStatus =
  | "free"
  | "active"
  | "past_due"
  | "canceled"
  | "lifetime";

export interface UseSubscriptionResult {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  isOwner: boolean;
  isPaid: boolean;
  isFounder: boolean;
  isLifetime: boolean;
  isTrainer: boolean;
  isLoading: boolean;
  periodEnd: Date | null;
}

export function useSubscription(): UseSubscriptionResult {
  const { data, isLoading } = trpc.stripe.getSubscriptionStatus.useQuery(
    undefined,
    {
      staleTime: 60_000, // re-fetch at most once per minute
      retry: false,
    }
  );

  // isOwner is set server-side — trusts the server response, not client logic
  const isOwner = data?.isOwner === true;
  const tier = (data?.subscriptionTier ?? "free") as SubscriptionTier;
  const status = (data?.subscriptionStatus ?? "free") as SubscriptionStatus;

  // Owner/admin always has full access — no Stripe check needed
  const isPaid =
    isOwner ||
    status === "active" ||
    status === "lifetime" ||
    tier === "founding_monthly" ||
    tier === "founding_lifetime" ||
    tier === "trainer";

  const isFounder =
    isOwner || tier === "founding_monthly" || tier === "founding_lifetime";

  const isLifetime =
    isOwner || tier === "founding_lifetime" || status === "lifetime";

  const isTrainer = isOwner || tier === "trainer";

  const periodEnd = data?.subscriptionCurrentPeriodEnd
    ? new Date(data.subscriptionCurrentPeriodEnd as unknown as string)
    : null;

  return {
    tier,
    status,
    isOwner,
    isPaid,
    isFounder,
    isLifetime,
    isTrainer,
    isLoading,
    periodEnd,
  };
}
